/**
 * InfinitSales - App Component
 * Main application with routing
 * 
 * UPDATED: 
 * - Fixed routing logic for complete onboarding flow
 * - /connection-setup and /agent-onboarding now accessible during onboarding
 * - Smart redirects based on user onboarding state
 * 
 * ✅ CORREÇÃO APLICADA:
 * - SmartRedirect não usa mais fallback cego para /inbox
 * - Se default_channel não estiver definido, redireciona para /onboarding
 * - Adicionado logs para debug
 * 
 * Flow: Register → ChannelSelection → ConnectionSetup → AgentOnboarding → Inbox
 */
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SSEProvider } from './contexts/SSEContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import ErrorBoundary from './components/ErrorBoundary';
// Sessão delegada: o onboarding é do DONO do workspace, nunca de quem visita.
import { isDelegated } from './utils/delegation';

// Pages - Auth
import Login from './pages/global/Login';
import Register from './pages/global/Register';
import Onboarding from './pages/global/Onboarding';
import ConnectionSetup from './pages/global/ConnectionSetup';

// Pages - Evolution (WhatsApp Local)
import Inbox from './pages/wa-personal/Inbox';

// Pages - WhatsApp Business
import WABusinessInbox from './pages/wa-business/Inbox';

// Pages - Instagram Direct
import InstagramInbox from './pages/instagram/Inbox';

// Pages - Facebook Messenger
import MessengerInbox from './pages/messenger/Inbox';
import WidgetInbox from './pages/widget/Inbox';
import WidgetConnectionsPage from './pages/widget/ConnectionsPage';

// Layout persistente das rotas de inbox e funis (frente C1, flag
// persistent_layout): a NavSidebar deixa de remontar a cada troca de canal.
import InboxLayout from './components/inbox/InboxLayout';

// Pages - Global (sempre no bundle principal: entrada da sessao e inbox)
import AccountPage from './pages/global/AccountPage';
import TagsKanbanStandalone from './pages/global/TagsKanbanStandalone';
import SavedMessagesPage from './pages/global/SavedMessagesPage';
import FollowUpsStandalone from './pages/global/FollowUpsStandalone';

// Conta Empresa (Fase 1.5) - modal global de boas-vindas para members
import MemberWelcomeModal from './components/MemberWelcomeModal';

// Conta Empresa (Etapa 2.2) - faixa fixa de sessao delegada (workspace alheio)
import DelegationBanner from './components/DelegationBanner';
import WorkspaceModeModal from './components/WorkspaceModeModal';

// Toast de progresso de campanhas e card do sino: globais, ficam no bundle.
import CampaignProgressToast from './components/campaigns/CampaignProgressToast';
import NotificationToaster from './components/notifications/NotificationToaster';
// Aviso "Sua sessão expirou" (flag REACT_APP_SSE_EXP_CHECK no build).
import SessionExpiredToast from './components/notifications/SessionExpiredToast';

// Tudo que NAO e inbox nem funil entra por React.lazy (frente C8): cada
// pagina vira um chunk proprio e so e baixada quando a rota abre. O bundle
// principal carregava a Home, a Central de Ajuda, o painel da Empresa (com
// recharts), as 4 telas de campanha etc. mesmo para quem so usa o inbox.
// Sem flag: e so carregamento, o comportamento das telas nao muda.
const AgentOnboarding = React.lazy(() => import('./pages/global/AgentOnboarding'));
const PlansPage = React.lazy(() => import('./pages/global/PlansPage'));
const Home = React.lazy(() => import('./pages/global/Home'));

// Central de Ajuda (publica): absorveu /tutorials e /integration-docs,
// que agora redirecionam para os artigos correspondentes.
const AjudaHomePage = React.lazy(() => import('./pages/global/ajuda/AjudaHomePage'));
const AjudaCategoryPage = React.lazy(() => import('./pages/global/ajuda/AjudaCategoryPage'));
const AjudaArticlePage = React.lazy(() => import('./pages/global/ajuda/AjudaArticlePage'));
const AgentTrainingPage = React.lazy(() => import('./pages/global/AgentTrainingPage'));
const AgentsStandalone = React.lazy(() => import('./pages/global/AgentsStandalone'));
const MetaLeadsPage = React.lazy(() => import('./pages/global/MetaLeadsPage'));
const CalendarStandalone = React.lazy(() => import('./pages/global/CalendarStandalone'));
const PrivacyPage = React.lazy(() => import('./pages/global/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/global/TermsPage'));
const EnterprisePage = React.lazy(() => import('./pages/global/EnterprisePage'));

// Conta Empresa (Fase 1): painel admin-only (traz o recharts dos paineis)
const EmpresaDashboard = React.lazy(() => import('./pages/empresa/EmpresaDashboard'));

// Campanhas (Fase 2.B): historico, disparo, wizard e fluxos
const CampaignsPage = React.lazy(() => import('./pages/global/CampaignsPage'));
const CampaignsEntry = React.lazy(() => import('./pages/global/CampaignsEntry'));
const CampaignBuilderPage = React.lazy(() => import('./pages/global/CampaignBuilderPage'));
const CampaignWorkflowsPage = React.lazy(() => import('./pages/global/CampaignWorkflowsPage'));

// Importar contatos (Fase 3.2): CSV/XLSX com stepper
const ContactsImportPage = React.lazy(() => import('./pages/global/ContactsImportPage'));

/**
 * ✅ CORREÇÃO: Smart Redirect Component
 * Redireciona baseado no estado do usuário SEM fallback cego
 */
const SmartRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check onboarding status
  const onboardingCompleted = user?.onboarding_completed ?? false;
  const defaultChannel = user?.default_channel;

  // ✅ DEBUG: Log para ajudar a identificar problemas
  console.log('[SmartRedirect] Estado do usuário:', {
    onboarding_completed: onboardingCompleted,
    default_channel: defaultChannel,
    user_id: user?.id
  });

  // Sessão delegada: o onboarding pertence ao dono do workspace. Visitante vai
  // direto para o inbox do canal dele (vazio, se ainda não conectou).
  if (isDelegated()) {
    return <Navigate to={defaultChannel === 'wa_business' ? '/wa-business' : '/inbox'} replace />;
  }

  // ✅ CORREÇÃO: Se não tiver canal selecionado, SEMPRE vai para onboarding
  if (!defaultChannel) {
    console.log('[SmartRedirect] Sem canal definido, indo para /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // Se onboarding não completado, continuar o fluxo
  if (!onboardingCompleted) {
    console.log('[SmartRedirect] Onboarding não completo, indo para /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // ✅ CORREÇÃO: Redirecionar baseado EXATAMENTE no canal salvo
  if (defaultChannel === 'wa_business') {
    console.log('[SmartRedirect] Canal wa_business, indo para /wa-business');
    return <Navigate to="/wa-business" replace />;
  }

  if (defaultChannel === 'wa_personal') {
    console.log('[SmartRedirect] Canal wa_personal, indo para /inbox');
    return <Navigate to="/inbox" replace />;
  }

  // ✅ CORREÇÃO: Se chegou aqui, canal desconhecido - ir para onboarding
  console.warn('[SmartRedirect] Canal desconhecido:', defaultChannel, '- indo para /onboarding');
  return <Navigate to="/onboarding" replace />;
};

/**
 * Protected Route Component
 * Handles authentication and onboarding checks
 */
const ProtectedRoute = ({ children, requiresOnboarding = true }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to complete onboarding.
  // SESSÃO DELEGADA: quem entrou no workspace de outra pessoa NÃO faz o
  // onboarding do dono. Sem esta guarda, entrar no workspace de um membro que
  // nunca conectou canal jogava o visitante na tela de onboarding do outro
  // (fundo escuro, sem saída aparente) — o "fica preto ao trocar de
  // workspace" relatado pela myCar em 18/08 (Juarez Pina e Gilberto Rocha,
  // onboarding_completed=false e default_channel=null).
  if (requiresOnboarding && !isDelegated()) {
    const onboardingCompleted = user?.onboarding_completed ?? false;
    const defaultChannel = user?.default_channel;

    // ✅ CORREÇÃO: Também verificar se tem canal definido
    if (!onboardingCompleted || !defaultChannel) {
      console.log('[ProtectedRoute] Onboarding incompleto ou sem canal, redirecionando');
      return <Navigate to="/onboarding" replace />;
    }
  }

  return children;
};

/**
 * Authenticated Route Component
 * Only checks authentication, no onboarding checks
 * Used for onboarding flow pages (connection-setup, agent-onboarding)
 */
const AuthenticatedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * MemberGuard — bloqueia rotas sensíveis para Conta Empresa members.
 *
 * Members são vendedores criados pelo admin que herdam agente, plano e
 * créditos da empresa. Eles não podem mexer em agentes (gerenciado pelo
 * admin), planos (cobrança é da empresa) ou docs de integração / API keys
 * (chave é da empresa). Tentativa via URL direta redireciona para o inbox.
 *
 * Aplicado em: /agent-training, /agents, /plans, /integration-docs.
 */
const MemberGuard = ({ children }) => {
  const { isCompanyMember, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (isCompanyMember) {
    return <Navigate to="/inbox" replace state={{
      flash: 'Esta área é gerenciada pelo administrador da empresa.',
    }} />;
  }
  return children;
};

/**
 * Onboarding Route Component
 * Only accessible if user hasn't completed onboarding yet
 */
const OnboardingRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const onboardingCompleted = user?.onboarding_completed ?? false;
  const defaultChannel = user?.default_channel;

  // Sessão delegada: nunca renderiza o onboarding do dono do workspace.
  if (isDelegated()) {
    return <Navigate to={defaultChannel === 'wa_business' ? '/wa-business' : '/inbox'} replace />;
  }

  // ✅ CORREÇÃO: Só redirecionar se AMBOS estiverem completos
  if (onboardingCompleted && defaultChannel) {
    console.log('[OnboardingRoute] Onboarding completo, redirecionando para inbox');
    if (defaultChannel === 'wa_business') {
      return <Navigate to="/wa-business" replace />;
    }
    return <Navigate to="/inbox" replace />;
  }

  return children;
};

/**
 * TutorialsRedirect — /tutorials virou artigo da Central de Ajuda.
 * Preserva os deep links por hash que o produto e o suporte já usavam.
 */
const TutorialsRedirect = () => {
  const { hash } = useLocation();
  const target = hash === '#meta-leads-forms'
    ? '/ajuda/integracoes/meta-lead-ads'
    : '/ajuda/conexoes/whatsapp-business';
  return <Navigate to={target} replace />;
};

/**
 * HomeOrRedirect Component
 * Shows Home for non-authenticated users, SmartRedirect for authenticated
 */
const HomeOrRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <SmartRedirect />;
  }

  return <Home />;
};

/**
 * Public Route Component
 * Redirects to appropriate page if already authenticated
 */
const PublicRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    const onboardingCompleted = user?.onboarding_completed ?? false;
    const defaultChannel = user?.default_channel;
    
    // ✅ DEBUG
    console.log('[PublicRoute] Usuário autenticado:', {
      onboarding_completed: onboardingCompleted,
      default_channel: defaultChannel
    });
    
    // Se não tem canal, vai para onboarding
    if (!defaultChannel) {
      return <Navigate to="/onboarding" replace />;
    }
    
    // Se onboarding não completado, continuar o fluxo
    if (!onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }

    // ✅ CORREÇÃO: Redirecionar baseado no canal salvo
    if (defaultChannel === 'wa_business') {
      return <Navigate to="/wa-business" replace />;
    }
    
    if (defaultChannel === 'wa_personal') {
      return <Navigate to="/inbox" replace />;
    }
    
    // Canal desconhecido
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

/**
 * Loading Screen Component
 */
const LoadingScreen = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#111b21',
    color: '#e9edef',
    gap: '16px',
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid #2a3942',
      borderTopColor: '#00a884',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }}></div>
    <span>Carregando...</span>
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

/**
 * App Routes
 */
const AppRoutes = () => {
  return (
    <Routes>
      {/* ================================
          PUBLIC ROUTES
          ================================ */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* ================================
          ONBOARDING FLOW ROUTES
          These routes are part of the onboarding flow
          and should be accessible during the process
          ================================ */}
      
      {/* Step 1: Channel Selection */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <Onboarding />
          </OnboardingRoute>
        }
      />
      
      {/* Step 2: Connection Setup (QR Code or OAuth) */}
      {/* Uses AuthenticatedRoute - only checks if logged in */}
      <Route
        path="/connection-setup"
        element={
          <AuthenticatedRoute>
            <ConnectionSetup />
          </AuthenticatedRoute>
        }
      />
      
      {/* Step 3: Agent Onboarding (Configure AI Agent) */}
      {/* Uses AuthenticatedRoute - only checks if logged in */}
      <Route
        path="/agent-onboarding"
        element={
          <AuthenticatedRoute>
            <AgentOnboarding />
          </AuthenticatedRoute>
        }
      />

      {/* ================================
          PROTECTED ROUTES (Require completed onboarding)
          ================================ */}
      
      {/* Rotas de inbox e de funis como filhas do InboxLayout (frente C1).
          Cada filha mantem o proprio ProtectedRoute; o layout so decide, pela
          flag persistent_layout, se a NavSidebar mora nele (e sobrevive a
          troca de canal) ou em cada shell, como hoje. */}
      <Route element={<InboxLayout />}>
        {/* WhatsApp Personal Inbox */}
        <Route
          path="/inbox"
          element={
            <ProtectedRoute>
              <Inbox />
            </ProtectedRoute>
          }
        />

        {/* WhatsApp Business Inbox */}
        <Route
          path="/wa-business"
          element={
            <ProtectedRoute>
              <WABusinessInbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wa-business/*"
          element={
            <ProtectedRoute>
              <WABusinessInbox />
            </ProtectedRoute>
          }
        />

        {/* Instagram Direct Inbox.
            Rota sempre montada; o gate real e o backend (INSTAGRAM_ENABLED), que
            responde 404 nas rotas do canal com a flag desligada: a tela mostra
            "conectar" e nao ha o que fazer. Gatear no roteador exigiria esperar o
            fetch de flags antes de renderizar QUALQUER rota. */}
        <Route
          path="/instagram"
          element={
            <ProtectedRoute>
              <InstagramInbox />
            </ProtectedRoute>
          }
        />

        {/* Facebook Messenger Inbox: mesmo racional do Instagram, rota sempre
            montada, gate real e o backend (MESSENGER_ENABLED responde 404). */}
        <Route
          path="/messenger"
          element={
            <ProtectedRoute>
              <MessengerInbox />
            </ProtectedRoute>
          }
        />

        {/* Widget do site (5o canal, 2026-09-23): mesmo racional dos canais da
            Meta, rota sempre montada, gate real e o backend (WIDGET_ENABLED
            responde 404). /widget/conexoes e a tela de cards (N widgets). */}
        <Route
          path="/widget"
          element={
            <ProtectedRoute>
              <WidgetInbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/widget/conexoes"
          element={
            <ProtectedRoute>
              <WidgetConnectionsPage />
            </ProtectedRoute>
          }
        />

        {/* Kanban de funis com rota propria: antes so existia dentro dos shells
            de wa-personal/wa-business, o que ejetava Instagram e Messenger para
            o inbox do WhatsApp (D6 do sweep 2B). Sem MemberGuard: o Kanban e
            leitura e movimentacao de etapa, liberadas para membro. */}
        <Route
          path="/funis"
          element={
            <ProtectedRoute>
              <TagsKanbanStandalone />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* ================================
          GLOBAL PROTECTED ROUTES
          ================================ */}
      <Route
        path="/plans"
        element={
          <AuthenticatedRoute>
            <MemberGuard>
              <PlansPage />
            </MemberGuard>
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <AuthenticatedRoute>
            <AccountPage />
          </AuthenticatedRoute>
        }
      />
      {/* Rotas antigas absorvidas pela Central de Ajuda. Ficam no ar porque o
          suporte já colou esses links (inclusive com hash) para clientes. */}
      <Route path="/integration-docs" element={<Navigate to="/ajuda/integracoes/integracao-via-api" replace />} />
      <Route path="/tutorials" element={<TutorialsRedirect />} />
      <Route
        path="/agent-training"
        element={
          <ProtectedRoute>
            <MemberGuard>
              <AgentTrainingPage />
            </MemberGuard>
          </ProtectedRoute>
        }
      />
      {/* Lista de agentes com rota própria: antes só existia dentro dos shells
          de wa-personal/wa-business, o que ejetava Instagram e Messenger. */}
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <MemberGuard>
              <AgentsStandalone />
            </MemberGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meta-leads"
        element={
          <ProtectedRoute>
            <MetaLeadsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved-messages"
        element={
          <ProtectedRoute>
            <SavedMessagesPage />
          </ProtectedRoute>
        }
      />

      {/* Follow-ups com rota própria — o badge do ChatWindow abre
          /followups?id=<id> (Ver detalhes). Deep-link tratado na FollowUpsPage. */}
      <Route
        path="/followups"
        element={
          <ProtectedRoute>
            <FollowUpsStandalone />
          </ProtectedRoute>
        }
      />

      {/* Calendário do workspace — eventos nativos + espelho Google Calendar
          (Composio). Visões dia/semana/mês, tipos de evento configuráveis. */}
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarStandalone />
          </ProtectedRoute>
        }
      />

      {/* ================================
          CONTA EMPRESA (Fase 1)
          Admin-only painel — EmpresaDashboard verifica role e redireciona
          quem não for admin para /
          ================================ */}
      <Route
        path="/empresa"
        element={
          <AuthenticatedRoute>
            <EmpresaDashboard />
          </AuthenticatedRoute>
        }
      />

      {/* ================================
          CAMPANHAS (Fase 2.B)
          Historico de campanhas em batch. Toast de progresso e montado
          globalmente abaixo via CampaignProgressToast — esta rota apenas
          mostra a lista clicavel.
          ================================ */}
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <CampaignsEntry />
          </ProtectedRoute>
        }
      />

      {/* Disparo simples (tela classica): lista, relatorios e bloqueios.
          Secundaria desde 28/08 — a entrada de Campanhas e a tela de fluxos. */}
      <Route
        path="/campaigns/disparos"
        element={
          <ProtectedRoute>
            <CampaignsPage />
          </ProtectedRoute>
        }
      />

      {/* Campanhas Inteligentes — wizard 4 steps com filtros AND/NOT,
          limite, modo IA opcional e templates reusaveis */}
      <Route
        path="/campaigns/new"
        element={
          <ProtectedRoute>
            <CampaignBuilderPage />
          </ProtectedRoute>
        }
      />

      {/* Fluxos de campanha (workflow de blocos, Etapa 3.6). O modo simples
          convive: /campaigns/new segue sendo o disparo comum. */}
      <Route
        path="/campaigns/fluxos"
        element={
          <ProtectedRoute>
            <CampaignWorkflowsPage />
          </ProtectedRoute>
        }
      />

      {/* ================================
          IMPORTAR CONTATOS (Fase 3.2)
          Pagina standalone com stepper de 3 passos (template, upload+map, commit).
          Atalho fica na header da TagsKanbanPage por enquanto.
          ================================ */}
      <Route
        path="/contacts/import"
        element={
          <ProtectedRoute>
            <ContactsImportPage />
          </ProtectedRoute>
        }
      />

      {/* ================================
          PUBLIC LANDING PAGE
          ================================ */}
      <Route
        path="/home"
        element={
          <PublicRoute>
            <Home />
          </PublicRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/para-empresas" element={<EnterprisePage />} />

      {/* ================================
          CENTRAL DE AJUDA (pública)
          Acessível logado e deslogado — o suporte cola esses links.
          ================================ */}
      <Route path="/ajuda" element={<AjudaHomePage />} />
      <Route path="/ajuda/:categoryId" element={<AjudaCategoryPage />} />
      <Route path="/ajuda/:categoryId/:slug" element={<AjudaArticlePage />} />

      {/* ================================
          DEFAULT / CATCH-ALL ROUTES
          ================================ */}
      <Route path="/" element={<HomeOrRedirect />} />
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
};

/**
 * Main App Component
 */
const App = () => {
  return (
    <BrowserRouter>
      {/* Rede de segurança global: qualquer erro de render em qualquer tela vira
          um cartão "Algo deu errado" com Recarregar — nunca mais a TELA PRETA que
          travava tudo (não conseguir voltar, criar nota/tarefa ou mover o lead). */}
      <ErrorBoundary name="root">
      <ThemeProvider>
        <AuthProvider>
          <SSEProvider>
            <NotificationsProvider>
              <div className="app">
                {/* Fallback dos chunks lazy (frente C8): a mesma tela de
                    carregamento da sessao, so enquanto o chunk baixa. */}
                <Suspense fallback={<LoadingScreen />}>
                  <AppRoutes />
                </Suspense>
                {/* Conta Empresa (Etapa 2.2): limpeza silenciosa de marcador
                    de delegação obsoleto (o indicador visual está no menu). */}
                <DelegationBanner />
                {/* Modo workspace: auto-entrada no login + modal de boas-vindas
                    com checkbox "entrar sempre neste workspace". */}
                <WorkspaceModeModal />
                {/* Conta Empresa: welcome modal shown once to fresh members */}
                <MemberWelcomeModal />
                {/* Toast global de progresso de campanhas — escuta SSE e
                    abre o modal de resultado ao clicar. Montado aqui (e nao
                    numa pagina) porque o usuario pode estar em qualquer tela
                    quando a campanha terminar e queremos avisar mesmo assim. */}
                <CampaignProgressToast />
                {/* Card no canto para notificacoes internas (sino) */}
                <NotificationToaster />
                {/* JWT vencido: aviso antes de cair no login. Fica fora da
                    fila do sino porque ela é zerada no logout. */}
                <SessionExpiredToast />
              </div>
            </NotificationsProvider>
          </SSEProvider>
        </AuthProvider>
      </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;